import {
  buildSummary,
  decodeDraft,
  decodePattern,
  decodeSummary,
  encodeDraft,
  encodePattern,
  encodeSummary,
  stampUpdatedAt,
  type Clock,
  type IdGenerator,
  type PatternDraft,
  type PatternRepository,
  type StorageFailure,
} from '@miyuki/domain';
import type { BraceletPatternDto, EntityId, PatternSummaryDto, Result } from '@miyuki/types';

/**
 * Adaptador de `localStorage` del puerto `PatternRepository`.
 *
 * No hay índice central: la lista se arma enumerando claves por prefijo. Un
 * índice sería un único punto de fallo capaz de esconder todos los diseños, y
 * RF-45 exige justo lo contrario. El resumen va en una clave aparte del patrón
 * para que abrir la lista lea unos pocos kilobytes y no todos los diseños
 * enteros.
 */

export const DESIGN_KEY_PREFIX = 'miyuki.design.';
export const DRAFT_KEY = 'miyuki.draft';

const SUMMARY_SUFFIX = '.summary';
const PATTERN_SUFFIX = '.pattern';

function summaryKey(id: EntityId): string {
  return `${DESIGN_KEY_PREFIX}${id}${SUMMARY_SUFFIX}`;
}

function patternKey(id: EntityId): string {
  return `${DESIGN_KEY_PREFIX}${id}${PATTERN_SUFFIX}`;
}

function ok<TValue>(value: TValue): Result<TValue, StorageFailure> {
  return { ok: true, value };
}

function fail<TValue>(error: StorageFailure): Result<TValue, StorageFailure> {
  return { ok: false, error };
}

/** `QuotaExceededError` cambia de nombre y de código según el navegador. */
function isQuotaExceeded(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  return (
    error.name === 'QuotaExceededError' ||
    error.name === 'NS_ERROR_DOM_QUOTA_REACHED' ||
    (error as { code?: number }).code === 22
  );
}

export interface LocalStoragePatternRepositoryDeps {
  readonly storage: Storage;
  readonly clock: Clock;
  readonly ids: IdGenerator;
}

export function createLocalStoragePatternRepository({
  storage,
  clock,
  ids,
}: LocalStoragePatternRepositoryDeps): PatternRepository {
  /* Toda lectura es tolerante: lo que no se entiende se omite, nunca se borra. */
  function readJson(key: string): unknown {
    try {
      const raw = storage.getItem(key);
      return raw === null ? null : (JSON.parse(raw) as unknown);
    } catch {
      return null;
    }
  }

  function writeJson(key: string, value: unknown): StorageFailure | null {
    try {
      storage.setItem(key, JSON.stringify(value));
      return null;
    } catch (error) {
      return isQuotaExceeded(error) ? { kind: 'quota-exceeded' } : { kind: 'unavailable' };
    }
  }

  function removeKey(key: string): void {
    try {
      storage.removeItem(key);
    } catch {
      // Un almacenamiento que no deja borrar tampoco dejó escribir.
    }
  }

  function summaryKeys(): string[] {
    try {
      return Object.keys(storage).filter(
        (key) => key.startsWith(DESIGN_KEY_PREFIX) && key.endsWith(SUMMARY_SUFFIX),
      );
    } catch {
      return [];
    }
  }

  function readSummaries(): PatternSummaryDto[] {
    return summaryKeys()
      .map((key) => decodeSummary(readJson(key)))
      .filter((result) => result.ok)
      .map((result) => result.value)
      .sort((left, right) => right.updatedAt.localeCompare(left.updatedAt));
  }

  /**
   * Escribe patrón y resumen como una unidad: si la segunda clave falla, se
   * deshace la primera. Un diseño a medias sería peor que no haberlo guardado,
   * porque aparecería en la lista y no se podría abrir.
   */
  function writeDesign(pattern: BraceletPatternDto): StorageFailure | null {
    const patternFailure = writeJson(patternKey(pattern.id), encodePattern(pattern));
    if (patternFailure) return patternFailure;

    const summaryFailure = writeJson(summaryKey(pattern.id), encodeSummary(buildSummary(pattern)));
    if (summaryFailure) {
      removeKey(patternKey(pattern.id));
      return summaryFailure;
    }

    return null;
  }

  function loadPattern(id: EntityId): Result<BraceletPatternDto, StorageFailure> {
    const raw = readJson(patternKey(id));
    if (raw === null) return fail({ kind: 'not-found', id });

    const decoded = decodePattern(raw);
    return decoded.ok ? ok(decoded.value) : fail({ kind: 'corrupt', id });
  }

  return {
    list: () => Promise.resolve(readSummaries()),

    findByName: (name) => {
      const wanted = name.trim();
      return Promise.resolve(readSummaries().find((summary) => summary.name === wanted) ?? null);
    },

    load: (id) => Promise.resolve(loadPattern(id)),

    save: (pattern) => {
      const stamped = stampUpdatedAt(pattern, clock);
      const failure = writeDesign(stamped);
      return Promise.resolve(failure ? fail(failure) : ok(stamped));
    },

    rename: (id, name) => {
      const current = loadPattern(id);
      if (!current.ok) return Promise.resolve(fail(current.error));

      const renamed = stampUpdatedAt({ ...current.value, name: name.trim() }, clock);
      const failure = writeDesign(renamed);
      return Promise.resolve(failure ? fail(failure) : ok(undefined));
    },

    duplicate: (id, name) => {
      const current = loadPattern(id);
      if (!current.ok) return Promise.resolve(fail(current.error));

      const copy = stampUpdatedAt({ ...current.value, id: ids.next(), name: name.trim() }, clock);
      const failure = writeDesign(copy);
      return Promise.resolve(failure ? fail(failure) : ok(copy));
    },

    remove: (id) => {
      removeKey(patternKey(id));
      removeKey(summaryKey(id));
      return Promise.resolve();
    },

    loadDraft: (): Promise<PatternDraft | null> => {
      const decoded = decodeDraft(readJson(DRAFT_KEY));
      return Promise.resolve(decoded.ok ? decoded.value : null);
    },

    saveDraft: (pattern, savedFrom) => {
      // No devuelve error a propósito: perder el autoguardado no debe
      // interrumpir a quien está diseñando.
      writeJson(DRAFT_KEY, encodeDraft(pattern, savedFrom));
      return Promise.resolve();
    },

    clearDraft: () => {
      removeKey(DRAFT_KEY);
      return Promise.resolve();
    },
  };
}
